import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { IAvailablePhoneNumber } from '../../interface'
import { formatPhoneNumber } from '../../utils'
import { CloseIcon } from '@forwardflow/icons'
import { PhoneNumberSearchInput } from '../PhoneNumberSearchInput'
import { ConfirmPurchaseNumbersModal } from '../modals/ConfirmPurchaseNumbersModal'
import {
   useGetAvailablePhoneNumbers,
   usePurchasePhoneNumbers,
} from '../../queries/phone-number-management.queries'
import { useAppStore } from '@forwardflow/store'
import { message } from 'antd'

interface BuyNewNumbersViewProps {
   setSelectedTab?: (tab: string) => void
   onNumbersSelected?: (numbers: IAvailablePhoneNumber[]) => void
   listMaxHeight?: string
}

export const BuyNewNumbersView = ({
   setSelectedTab,
   onNumbersSelected,
   listMaxHeight = 'calc(100vh - 500px)',
}: BuyNewNumbersViewProps) => {
   const { currentOrganization } = useAppStore()
   const [searchQuery, setSearchQuery] = useState('')
   const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
   const [selectedNumbers, setSelectedNumbers] = useState<
      IAvailablePhoneNumber[]
   >([])
   const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
   const [isPurchasing, setIsPurchasing] = useState(false)

   const { purchasePhoneNumbers } = usePurchasePhoneNumbers()

   // Debounce search query
   useEffect(() => {
      const timer = setTimeout(() => {
         setDebouncedSearchQuery(searchQuery)
      }, 500)

      return () => clearTimeout(timer)
   }, [searchQuery])

   const { data: availableNumbers = [], isLoading: isLoadingAvailableNumbers } =
      useGetAvailablePhoneNumbers(
         debouncedSearchQuery
            ? { limit: 1000, search: debouncedSearchQuery }
            : { limit: 50 }
      ) as { data: IAvailablePhoneNumber[]; isLoading: boolean }

   const handleSelectNumber = (number: IAvailablePhoneNumber) => {
      const isSelected = selectedNumbers.some(
         (selectedNumber) => selectedNumber.phoneNumber === number.phoneNumber
      )

      const updatedNumbers = isSelected
         ? selectedNumbers.filter(
              (selectedNumber) =>
                 selectedNumber.phoneNumber !== number.phoneNumber
           )
         : [
              ...selectedNumbers,
              { phoneNumber: number.phoneNumber, locality: number.locality },
           ]

      setSelectedNumbers(updatedNumbers as IAvailablePhoneNumber[])
      onNumbersSelected?.(updatedNumbers as IAvailablePhoneNumber[])
   }

   const handleOpenConfirmModal = () => {
      if (selectedNumbers.length > 0) {
         setIsConfirmModalOpen(true)
      }
   }

   const handleConfirmPurchase = async () => {
      setIsPurchasing(true)
      try {
         const orgId = currentOrganization || ''
         const phoneNumbersToPurchase = selectedNumbers.map(
            (number) => number.phoneNumber
         )

         await purchasePhoneNumbers(orgId, phoneNumbersToPurchase)
         
         message.success(
            `Successfully purchased ${phoneNumbersToPurchase.length} phone number${
               phoneNumbersToPurchase.length !== 1 ? 's' : ''
            }`
         )

         setIsConfirmModalOpen(false)
         setSelectedTab?.('list-of-numbers')
         setSelectedNumbers([])
      } catch (error) {
         console.error('Error purchasing phone numbers:', error)
         message.error('Failed to purchase phone numbers. Please try again.')
      } finally {
         setIsPurchasing(false)
      }
   }

   const handleCloseModal = () => {
      if (!isPurchasing) {
         setIsConfirmModalOpen(false)
      }
   }

   return (
      <div className="flex flex-col h-full">
         {/* Fixed Header Section */}
         <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col gap-y-6 flex-shrink-0"
         >
            <div className="p-4 bg-white/[0.05] mr-3 rounded-lg space-y-5">
               <div className="flex items-center justify-between border-b border-white/10 pb-4">
                  <p className="text-white text-lg font-medium">
                     Purchase new phone numbers
                  </p>
                  <div className="flex items-center gap-x-2">
                     <motion.button
                        key="cancel-button"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => {
                           setSelectedTab?.('list-of-numbers')
                           setSelectedNumbers([])
                        }}
                        disabled={isPurchasing}
                        className="bg-black/20 rounded-[4px] px-6 py-3 text-white hover:text-white/70 font-medium hover:bg-black/30 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                        Cancel
                     </motion.button>
                     <motion.button
                        disabled={selectedNumbers.length === 0 || isPurchasing}
                        key="confirm-button"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -10 }}
                        transition={{ duration: 0.2 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={handleOpenConfirmModal}
                        className="bg-[#9061DA66] rounded-[4px] px-6 py-3 text-white hover:text-white/70 font-medium hover:bg-black/40 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-x-2 justify-center"
                     >
                        {isPurchasing && (
                           <motion.div
                              animate={{ rotate: 360 }}
                              transition={{
                                 duration: 1,
                                 repeat: Infinity,
                                 ease: 'linear',
                              }}
                              className="w-4 h-4 border-2 border-white border-t-transparent rounded-full"
                           />
                        )}
                        {isPurchasing ? 'Processing...' : 'Confirm'}
                     </motion.button>
                  </div>
               </div>
               {/* Selected Numbers Section */}
               {selectedNumbers.length > 0 ? (
                  <div className="pr-3">
                     <p className="text-white text-sm font-medium mb-3">
                        Selected Numbers ({selectedNumbers.length})
                     </p>
                     <div className="flex flex-wrap gap-2">
                        {selectedNumbers.map((number) => (
                           <div
                              key={number.phoneNumber}
                              className="flex items-center gap-x-2 bg-[#D9D9D9]/10 rounded-lg px-4 py-2 pr-2"
                           >
                              <span className="text-white text-sm">
                                 {formatPhoneNumber(number.phoneNumber)}
                              </span>
                              <button
                                 onClick={() => handleSelectNumber(number)}
                                 className="text-white/60 hover:text-white"
                              >
                                 <CloseIcon className="w-3 h-3" />
                              </button>
                           </div>
                        ))}
                     </div>
                  </div>
               ) : (
                  <div className="flex items-center justify-center py-6">
                     <p className="text-white/40 text-sm">
                        No numbers selected
                     </p>
                  </div>
               )}
            </div>

            <PhoneNumberSearchInput
               value={searchQuery}
               onChange={setSearchQuery}
               placeholder="Search by area code or location"
            />

            <div className="flex items-center justify-between pr-3 mt-6">
               <p className="text-white text-sm font-medium">
                  Available numbers to purchase
               </p>
            </div>
         </motion.div>

         {/* Scrollable List Section */}
         <div
            className="overflow-y-auto mt-2"
            style={{ maxHeight: listMaxHeight }}
         >
            <div className="flex flex-col gap-y-2 pr-3">
               {isLoadingAvailableNumbers ? (
                  <div className="flex flex-col items-center justify-center py-12 gap-y-4">
                     <motion.div
                        animate={{ rotate: 360 }}
                        transition={{
                           duration: 1,
                           repeat: Infinity,
                           ease: 'linear',
                        }}
                        className="w-8 h-8 border-2 border-[#9061DA] border-t-transparent rounded-full"
                     />
                     <p className="text-white/60 text-sm">
                        Searching for available numbers...
                     </p>
                  </div>
               ) : availableNumbers.length > 0 ? (
                  availableNumbers.map((number, index) => (
                     <div
                        key={index}
                        className="flex items-center justify-between p-4 bg-white/[0.05] rounded-lg"
                     >
                        <div>
                           <p className="text-white font-medium">
                              {formatPhoneNumber(number.phoneNumber || '')}
                           </p>
                           <p className="text-white/70 text-sm">
                              {number.locality || 'Unknown'}
                           </p>
                        </div>
                        <button
                           onClick={() => handleSelectNumber(number)}
                           className="w-24 h-10 transition-all border rounded border-primary bg-primary/20 hover:bg-primary"
                        >
                           {selectedNumbers.some(
                              (n) => n.phoneNumber === number.phoneNumber
                           )
                              ? 'Remove'
                              : 'Select'}
                        </button>
                     </div>
                  ))
               ) : (
                  <div className="flex items-center justify-center py-12">
                     <p className="text-white/40 text-sm">
                        {searchQuery
                           ? `No available numbers found for "${searchQuery}"`
                           : 'Search for available phone numbers to purchase'}
                     </p>
                  </div>
               )}
            </div>
         </div>

         {/* Confirmation Modal */}
         <ConfirmPurchaseNumbersModal
            isOpen={isConfirmModalOpen}
            onClose={handleCloseModal}
            onConfirm={handleConfirmPurchase}
            numberCount={selectedNumbers.length}
            isLoading={isPurchasing}
         />
      </div>
   )
}
